/**
 * Shared confirm-delete dialog.
 *
 * Use this for any destructive delete action that should prompt the user
 * before proceeding. Provides a consistent title/body/footer layout with
 * Cancel + Delete buttons.
 */

import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'

export interface ConfirmDeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Title shown at top of dialog, e.g. "Delete attribute?" */
  title: string
  /** Optional body text. Defaults to a generic "cannot be undone" message. */
  description?: React.ReactNode
  /** Label for the confirm button. Defaults to "Delete". */
  confirmLabel?: string
  /** Called when the user confirms the delete. */
  onConfirm: () => void
  /** Disables the confirm button while the delete is in flight. */
  isPending?: boolean
  /** When false, closes only via Cancel/Delete (no backdrop, Escape, or X). */
  dismissible?: boolean
}

export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Delete',
  onConfirm,
  isPending,
  dismissible,
}: ConfirmDeleteDialogProps) {
  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      pendingLabel="Deleting..."
      destructive
      isPending={isPending}
      dismissible={dismissible}
      onConfirm={onConfirm}
    />
  )
}
